import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import {
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Description as BillIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Loading } from '../../components/Layout';
import tenantService from '../../services/tenantService';
import authService from '../../services/authService';
import billService from '../../services/billService';
import penaltyService from '../../services/penaltyService';

// StatCard Component - Must be outside to avoid React error #130
const StatCard = ({ title, value, subtitle, icon, color, bgcolor }) => (
  <Card sx={{ height: '100%', bgcolor: bgcolor || 'background.paper' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="h3" component="h2" fontWeight="bold" color={color || 'text.primary'}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: color, fontSize: 56, opacity: 0.8 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const TenantDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    unpaidInvoices: 0,
    totalAmountDue: 0,
    roomInfo: null,
    recentInvoices: [],
    creditBalance: 0,
  });
  const [billStats, setBillStats] = useState({
    pendingBills: 0,
    paidBills: 0,
    totalPending: 0,
  });
  const [penalties, setPenalties] = useState({
    count: 0,
    totalAmount: 0,
    items: [],
  });
  const currentUser = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 Fetching tenant dashboard data...');
      
      // Fetch tenant's dashboard data, bills, and penalties
      const [dashboardRes, billsRes, penaltiesRes] = await Promise.all([
        tenantService.getTenantDashboard(),
        billService.getMyBills().catch((err) => {
          console.warn('⚠️ Bills fetch failed:', err);
          return { data: [] };
        }),
        penaltyService.getMyPenalties().catch((err) => {
          console.warn('⚠️ Penalties fetch failed:', err);
          return { data: { penalties: [], total_penalty_amount: 0, penalty_count: 0 } };
        }),
      ]);
      
      console.log('✅ Dashboard API Response:', dashboardRes);
      console.log('📊 Dashboard Data:', dashboardRes.data);
      
      const data = dashboardRes.data;
      const bills = billsRes.data || [];
      
      // Log invoice data for debugging
      console.log('📄 Invoices Object:', data.invoices);
      console.log('📋 Recent Invoices Array:', data.invoices?.recent_invoices);
      console.log('📋 Recent Invoices Length:', data.invoices?.recent_invoices?.length || 0);
      
      if (!data.invoices?.recent_invoices || data.invoices.recent_invoices.length === 0) {
        console.error('❌ No recent invoices found in API response!');
        console.error('🔍 Full response data:', JSON.stringify(data, null, 2));
      } else {
        console.log('✅ Found', data.invoices.recent_invoices.length, 'recent invoices');
      }
      
      // Calculate bill statistics
      const pendingBills = bills.filter(b => b.status === 'pending' || b.status === 'overdue').length;
      const paidBills = bills.filter(b => b.status === 'paid').length;
      const totalPending = bills
        .filter(b => b.status === 'pending' || b.status === 'overdue')
        .reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
      
      const newStats = {
        totalInvoices: data.invoices?.total || 0,
        unpaidInvoices: data.invoices?.unpaid || 0,
        totalAmountDue: data.invoices?.amount_due || 0,
        roomInfo: data.room || null,
        recentInvoices: data.invoices?.recent_invoices || [],
        creditBalance: data.credit_balance || 0,
      };
      
      console.log('📦 Setting stats:', newStats);
      console.log('📋 recentInvoices being set:', newStats.recentInvoices);
      
      setStats(newStats);
      setBillStats({
        pendingBills,
        paidBills,
        totalPending,
      });
      
      // Set penalties
      const penaltiesData = penaltiesRes.data;
      setPenalties({
        count: penaltiesData.penalty_count || 0,
        totalAmount: penaltiesData.total_penalty_amount || 0,
        items: penaltiesData.penalties || [],
      });
      
      console.log('✅ Dashboard data loaded successfully');
    } catch (error) {
      console.error('❌ Failed to fetch dashboard data:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Show user-friendly error
      if (error.response?.status === 401) {
        console.error('🔐 Authentication error - user may need to log in again');
      } else if (error.response?.status === 403) {
        console.error('🚫 Authorization error - user may not have tenant role');
      } else if (error.response?.status === 404) {
        console.error('🔍 Tenant profile not found');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message="Loading dashboard..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Tenant Dashboard"
        subtitle={`Welcome back, ${currentUser?.full_name || 'Tenant'}!`}
      />

      {/* Alert Banners */}
      {penalties.count > 0 && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}
        >
          ⚠️ You have {penalties.count} late payment penalt{penalties.count > 1 ? 'ies' : 'y'} totaling KES {penalties.totalAmount.toLocaleString()}. Pay outstanding invoices to avoid additional charges.
        </Alert>
      )}
      {stats.totalAmountDue > 0 && (
        <Alert 
          severity="error" 
          icon={<WarningIcon />}
          sx={{ mb: 3, fontSize: '1rem', fontWeight: 500 }}
        >
          You have KES {stats.totalAmountDue.toLocaleString()} in outstanding payments. Please pay to avoid penalties.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="My Room"
            value={stats.roomInfo?.room_number || 'N/A'}
            subtitle={stats.roomInfo?.property_name || 'Not Assigned'}
            icon={<HomeIcon fontSize="inherit" />}
            color="primary.main"
            bgcolor="#e3f2fd"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Credit Balance"
            value={`KES ${stats.creditBalance.toLocaleString()}`}
            subtitle={stats.creditBalance > 0 ? 'Available for future invoices' : 'No credit available'}
            icon={<PaymentIcon fontSize="inherit" />}
            color={stats.creditBalance > 0 ? 'success.main' : 'text.secondary'}
            bgcolor={stats.creditBalance > 0 ? '#e8f5e9' : '#f5f5f5'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Amount Due"
            value={`KES ${stats.totalAmountDue.toLocaleString()}`}
            subtitle={stats.totalAmountDue > 0 ? 'Pay outstanding bills' : 'Up to date!'}
            icon={<WarningIcon fontSize="inherit" />}
            color={stats.totalAmountDue > 0 ? 'error.main' : 'success.main'}
            bgcolor={stats.totalAmountDue > 0 ? '#ffebee' : '#e8f5e9'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Rent"
            value={`KES ${(stats.roomInfo?.monthly_rent || 0).toLocaleString()}`}
            subtitle="Per month"
            icon={<HomeIcon fontSize="inherit" />}
            color="info.main"
            bgcolor="#e0f7fa"
          />
        </Grid>
      </Grid>

      {/* Information Panels */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <CheckCircleIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={600}>
                Rental Details
              </Typography>
            </Box>
            <Box sx={{ '& > *': { py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } } }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Room</Typography>
                <Typography variant="body2" fontWeight={600}>{stats.roomInfo?.room_number || 'N/A'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Property</Typography>
                <Typography variant="body2" fontWeight={600}>{stats.roomInfo?.property_name || 'N/A'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Monthly Rent</Typography>
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  KES {(stats.roomInfo?.monthly_rent || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip 
                  label={currentUser?.is_active ? 'Active' : 'Inactive'} 
                  color={currentUser?.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center">
                <BillIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Utility Bills
                </Typography>
              </Box>
              <Button 
                variant="outlined"
                size="small" 
                onClick={() => navigate('/tenant/bills')}
              >
                View All
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: billStats.pendingBills > 0 ? '#fff3e0' : '#e8f5e9', border: '1px solid', borderColor: billStats.pendingBills > 0 ? 'warning.light' : 'success.light' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom fontWeight={500}>
                      Pending Bills
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color={billStats.pendingBills > 0 ? 'warning.main' : 'success.main'}>
                      {billStats.pendingBills}
                    </Typography>
                    {billStats.pendingBills > 0 && (
                      <Typography variant="body2" color="warning.dark" fontWeight={500} sx={{ mt: 0.5 }}>
                        KES {billStats.totalPending.toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid', borderColor: 'success.light' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom fontWeight={500}>
                      Paid Bills
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {billStats.paidBills}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Box display="flex" alignItems="center">
                <ReceiptIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Recent Invoices
                </Typography>
              </Box>
              <Button 
                variant="outlined"
                size="small" 
                onClick={() => navigate('/tenant/invoices')}
              >
                View All
              </Button>
            </Box>
            
            {stats.recentInvoices.length === 0 ? (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  No invoices found. This could mean:
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    <li>Your invoices haven't been generated yet</li>
                    <li>There may be a connection issue</li>
                  </ul>
                  Please check the browser console (F12) for more details or contact support.
                </Alert>
                <Typography variant="caption" color="textSecondary" display="block">
                  Debug Info: Total Invoices: {stats.totalInvoices}, Recent Array Length: {stats.recentInvoices?.length || 0}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Invoice</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Paid</strong></TableCell>
                      <TableCell><strong>Balance</strong></TableCell>
                      <TableCell><strong>Due Date</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentInvoices.map((invoice) => {
                      const isPartiallyPaid = invoice.amount_paid > 0 && invoice.remaining_balance > 0;
                      const isOverpaid = invoice.is_overpaid || false;
                      const creditApplied = invoice.credit_applied || 0;
                      
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {invoice.invoice_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              KES {invoice.amount.toLocaleString()}
                            </Typography>
                            {invoice.penalty_amount > 0 && (
                              <Typography variant="caption" color="error" display="block">
                                +KES {invoice.penalty_amount.toLocaleString()} penalty
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              color={invoice.amount_paid > 0 ? 'success.main' : 'text.secondary'}
                              fontWeight={invoice.amount_paid > 0 ? 600 : 400}
                            >
                              KES {invoice.amount_paid.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isOverpaid || creditApplied > 0 ? (
                              <Box>
                                <Chip
                                  label={`Credit: KES ${creditApplied.toLocaleString()}`}
                                  color="success"
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                />
                              </Box>
                            ) : (
                              <Typography 
                                variant="body2" 
                                color={invoice.remaining_balance > 0 ? 'warning.main' : 'success.main'}
                                fontWeight={600}
                              >
                                KES {invoice.remaining_balance.toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(invoice.due_date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status.toUpperCase()}
                              color={invoice.status === 'paid' ? 'success' : invoice.status === 'partial_paid' ? 'info' : 'warning'}
                              size="small"
                            />
                            {isPartiallyPaid && (
                              <Chip
                                label="Partial"
                                color="info"
                                size="small"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                            {isOverpaid && (
                              <Chip
                                label="Overpaid"
                                color="success"
                                size="small"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TenantDashboard;
